import LiveFeedback from './LiveFeedback';
import { FormEvent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import api from '../../../../Component/axios_Interceptor/api';
import url, { SERVER_ADDRESS } from '../../../../Component/server';
import { AdminActionButton } from '../../../../Component/UI/AdminPageHeader';
import { publishChatMute } from '../../../Chat/useChatMuteMask';
import { publishLiveBan } from '../../../Chat/useChatRestrictions';

type Member = { user_seq: number; nickname: string; name?: string; ban_at?: string | null; muted_at?: string | null };
const time = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
export default function LiveModerationList({ kind, onBusyChange }: { kind: 'ban' | 'mute'; onBusyChange?: (busy: boolean) => void }) {
  const label = kind === 'ban' ? '라이브 차단' : '채팅 금지';
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState({ search: '', page: 1, revision: 0 });
  const [result, setResult] = useState<{ userList: Member[]; total: number } | null>(null);
  const [loading, setLoading] = useState(false), [error, setError] = useState(''), [message, setMessage] = useState('');
  const [saving, setSaving] = useState<number | null>(null);
  const pending = useRef(false);
  useEffect(() => {
    let active = true; setLoading(true); setError(''); setResult(null);
    api.get(kind === 'ban' ? url.Communication_url.ban_user_list : `${SERVER_ADDRESS}relation/chat-muted-user-list`, {
      params: { current_page: query.page, per_page: 20, search_key: 'nickname', search_value: query.search },
    }).then(({ data }) => {
      if (!active) return;
      if (!Array.isArray(data?.userList) || !Number.isInteger(data.total) || data.total < 0) throw new Error('Invalid member list');
      const lastPage = Math.max(1, Math.ceil(data.total / 20));
      if (query.page > lastPage) { setQuery(current => ({ ...current, page: lastPage })); return; }
      setResult(data);
    }).catch(() => { if (active) setError(`${label} 목록을 불러오지 못했습니다. 다시 시도해주세요.`); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [kind, label, query]);
  const release = async (member: Member) => {
    if (pending.current) return;
    pending.current = true; onBusyChange?.(true); setSaving(member.user_seq); setError(''); setMessage('');
    try {
      const { data } = kind === 'ban'
        ? await api.delete(url.Communication_url.user_unban, { data: { nickname: member.nickname } })
        : await api.post(`${SERVER_ADDRESS}realtime/chat-mute`, { nickname: member.nickname, enabled: false }, { headers: { 'Content-Type': 'application/json' } });
      if (kind === 'ban' ? data !== '성공' : data?.muted !== false || data?.nickname !== member.nickname) throw new Error('Unconfirmed release');
      if (kind === 'mute') publishChatMute(member.nickname, false);
      else publishLiveBan(member.nickname, false);
      setMessage(`${member.nickname} 님의 ${label} 상태를 해제했습니다.`);
      setQuery(current => ({ ...current, revision: current.revision + 1 }));
    } catch (failure: any) { setError(failure?.response?.data?.message || `${label} 상태를 해제하지 못했습니다. 다시 시도해주세요.`); }
    finally { pending.current = false; onBusyChange?.(false); setSaving(null); }
  };
  const search = (event: FormEvent) => { event.preventDefault(); setMessage(''); setQuery(current => ({ search: draft.trim(), page: 1, revision: current.revision + 1 })); };
  return <Content>
        <Search data-admin-search="" onSubmit={search}>
          <input aria-label={`${label} 목록 닉네임 검색`} placeholder="닉네임을 검색해주세요." value={draft} disabled={saving !== null}
            onChange={event => setDraft(event.target.value)} />
          <AdminActionButton type="submit" disabled={loading || saving !== null}>검색</AdminActionButton>
        </Search>
        {loading && <p role="status">목록을 불러오는 중입니다.</p>}
        {error && <LiveFeedback role="alert">{error}</LiveFeedback>}
        {message && <LiveFeedback role="status">{message}</LiveFeedback>}
        {result && <>
          <p>총 {result.total}명</p>
          {!result.userList.length && <p>해당하는 고객이 없습니다.</p>}
          <TableScroll><Table><thead><tr><th>닉네임</th><th>이름</th><th>{label} 시간 (한국시간)</th><th>관리</th></tr></thead>
            <tbody>{result.userList.map(member => { const stamp = kind === 'ban' ? member.ban_at : member.muted_at;
              const applied = stamp ? new Date(stamp) : null;
              return <tr key={member.user_seq}><td>{member.nickname}</td><td>{member.name || '—'}</td>
                <td>{applied && Number.isFinite(applied.getTime()) ? time.format(applied) : '기록 없음'}</td>
                <td><AdminActionButton type="button" aria-label={`${member.nickname} ${label} 해제`} disabled={saving !== null}
                  onClick={() => { void release(member); }}>{saving === member.user_seq ? '해제 중…' : `${label} 해제`}</AdminActionButton></td></tr>;
            })}</tbody></Table></TableScroll>
          <p>적용 시간이 저장되지 않은 기존 항목은 기록 없음으로 표시됩니다.</p>
          {result.total > 20 && <Pages>
            <AdminActionButton type="button" disabled={loading || saving !== null || query.page === 1} onClick={() => setQuery(current => ({ ...current, page: current.page - 1 }))}>이전</AdminActionButton>
            <span>{query.page} / {Math.ceil(result.total / 20)}</span>
            <AdminActionButton type="button" disabled={loading || saving !== null || query.page * 20 >= result.total} onClick={() => setQuery(current => ({ ...current, page: current.page + 1 }))}>다음</AdminActionButton>
          </Pages>}
        </>}
        {error && !result && <AdminActionButton type="button" disabled={loading || saving !== null} onClick={() => setQuery(current => ({ ...current, revision: current.revision + 1 }))}>다시 불러오기</AdminActionButton>}
      </Content>
  ;
}
const Content = styled.div`
  display: flex; flex-direction: column; gap: var(--space-3);
  p { margin: 0; font-size: var(--text-body); color: var(--color-text-secondary); }
  p[role="alert"] { color: var(--color-text-danger); }
`;
const Search = styled.form`
  display: flex; gap: var(--space-2);
  input { box-sizing: border-box; flex: 1; min-width: 0; min-height: var(--control-lg); padding: 0 var(--space-3);
    border: 1px solid var(--color-border-default); border-radius: var(--radius-md); background: var(--color-bg-surface); color: var(--color-text-primary); font: inherit; }
  input:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
`;
const TableScroll = styled.div`overflow-x: auto;`;
const Table = styled.table`
  width: 100%; min-width: 560px; border-collapse: collapse; font-size: var(--text-body);
  th, td { padding: var(--space-3); text-align: left; border-bottom: 1px solid var(--color-border-default); }
  th { color: var(--color-text-secondary); font-weight: 600; white-space: nowrap; }
  td { overflow-wrap: anywhere; }
  td:nth-child(3) { white-space: nowrap; font-variant-numeric: tabular-nums; }
`;
const Pages = styled.div`display: flex; align-items: center; justify-content: center; gap: var(--space-3);`;
